/**
 * Client Types
 *
 * Type definitions for clients.
 */

export enum PriceLevel {
  RETAIL = 'retail',
  WHOLESALE = 'wholesale',
  DISTRIBUTOR = 'distributor',
}

export enum IdentificationType {
  CEDULA_FISICA = '01',
  CEDULA_JURIDICA = '02',
  DIMEX = '03',
  NITE = '04',
  EXTRANJERO = '05',
}

export const IDENTIFICATION_TYPE_LABELS: Record<IdentificationType, string> = {
  [IdentificationType.CEDULA_FISICA]: 'Cédula Física',
  [IdentificationType.CEDULA_JURIDICA]: 'Cédula Jurídica',
  [IdentificationType.DIMEX]: 'DIMEX',
  [IdentificationType.NITE]: 'NITE',
  [IdentificationType.EXTRANJERO]: 'Extranjero',
}

/**
 * Validation patterns per identification type (Hacienda Costa Rica v4.4):
 * 01 Física = 9 díg., 02 Jurídica = 10, 03 DIMEX = 11-12, 04 NITE = 10,
 * 05 Extranjero = 1-20 alfanumérico.
 */
export const IDENTIFICATION_PATTERNS: Record<IdentificationType, RegExp> = {
  [IdentificationType.CEDULA_FISICA]: /^\d{9}$/,
  [IdentificationType.CEDULA_JURIDICA]: /^\d{10}$/,
  [IdentificationType.DIMEX]: /^\d{11,12}$/,
  [IdentificationType.NITE]: /^\d{10}$/,
  [IdentificationType.EXTRANJERO]: /^.{1,20}$/,
}

/** i18n keys for the per-type validation error message. */
export const IDENTIFICATION_ERROR_KEYS: Record<IdentificationType, string> = {
  [IdentificationType.CEDULA_FISICA]: 'validation.invalidCedula',
  [IdentificationType.CEDULA_JURIDICA]: 'validation.invalidCedulaJuridica',
  [IdentificationType.DIMEX]: 'validation.invalidDimex',
  [IdentificationType.NITE]: 'validation.invalidNite',
  [IdentificationType.EXTRANJERO]: 'validation.invalidExtranjero',
}

export const PRICE_LEVEL_LABELS: Record<PriceLevel, string> = {
  [PriceLevel.RETAIL]: 'Detalle',
  [PriceLevel.WHOLESALE]: 'Mayorista',
  [PriceLevel.DISTRIBUTOR]: 'Distribuidor',
}

/**
 * Exoneration document types according to Costa Rica Hacienda
 */
export enum ExonerationType {
  COMPRA_AUTORIZADA = '01',
  VENTAS_EXENTAS = '02',
  AUTORIZADO_LEY = '03',
  EXENCIONES_HACIENDA = '04',
  TRANSITORIO_V = '05',
  TRANSITORIO_IX = '06',
  TRANSITORIO_XVII = '07',
  OTROS = '99',
}

export const EXONERATION_TYPE_LABELS: Record<ExonerationType, string> = {
  [ExonerationType.COMPRA_AUTORIZADA]: 'Compras Autorizadas',
  [ExonerationType.VENTAS_EXENTAS]: 'Ventas Exentas a Diplomáticos',
  [ExonerationType.AUTORIZADO_LEY]: 'Autorizado por Ley Especial',
  [ExonerationType.EXENCIONES_HACIENDA]: 'Exenciones Dir. Gral. de Hacienda',
  [ExonerationType.TRANSITORIO_V]: 'Transitorio V',
  [ExonerationType.TRANSITORIO_IX]: 'Transitorio IX',
  [ExonerationType.TRANSITORIO_XVII]: 'Transitorio XVII',
  [ExonerationType.OTROS]: 'Otros',
}

/**
 * Hacienda 2-digit codes for the institution that issues the exoneration
 * document. Maps to nameInstitution in the Alanube payload.
 */
export const EXONERATION_INSTITUTION_OPTIONS: { value: string; label: string }[] = [
  { value: '01', label: 'Ministerio de Hacienda' },
  { value: '02', label: 'Ministerio de Agricultura y Ganadería' },
  { value: '03', label: 'Ministerio de Economía Industria y Comercio' },
  { value: '04', label: 'Cruz Roja Costarricense' },
  { value: '05', label: 'Benemérito Cuerpo de Bomberos' },
  { value: '06', label: 'Asociaciones de Desarrollo Comunal' },
  { value: '07', label: 'Junta de Administración Portuaria y Desarrollo Económico de la Vertiente Atlántica' },
  { value: '08', label: 'Caja Costarricense del Seguro Social' },
  { value: '09', label: 'Comité Cantonal de Deportes y Recreación' },
  { value: '10', label: 'Cuerpo de Bomberos de Costa Rica' },
  { value: '11', label: 'Patronato Nacional de la Infancia' },
  { value: '12', label: 'Universidades estatales' },
  { value: '99', label: 'Otros' },
]

export interface Client {
  id: number
  name: string
  type_identification: string
  identification: string
  email?: string
  phone?: string
  celphone?: string
  address?: string
  province?: string
  canton?: string
  credit_limit: number
  payment_days: number
  credit_blocked?: boolean
  current_balance: number
  // Exoneration fields (Hacienda)
  is_exonerated?: boolean
  exoneration_type?: string
  exoneration_number?: string
  exoneration_institution?: string
  exoneration_institution_code?: string
  exoneration_date?: string
  exoneration_percentage?: number
  // Commercial activity (Hacienda v4.4)
  activity_code?: string
  activity_code_name?: string
  deleted_at?: string | null
  bill_count?: number
  created_at?: string
  updated_at?: string
}

export interface ClientCreate {
  name: string
  type_identification: string
  identification: string
  email?: string
  phone?: string
  celphone?: string
  address?: string
  province?: string
  canton?: string
  credit_limit?: number
  payment_days?: number
  credit_blocked?: boolean
  // Exoneration fields
  is_exonerated?: boolean
  exoneration_type?: string
  exoneration_number?: string
  exoneration_institution?: string
  exoneration_institution_code?: string
  exoneration_date?: string
  exoneration_percentage?: number
  // Commercial activity (null clears it on update)
  activity_code?: string | null
  activity_code_name?: string | null
}

export interface ClientUpdate extends Partial<ClientCreate> {}

export interface ClientFilters {
  search?: string
  price_level?: PriceLevel
  with_credit?: boolean
  is_active?: boolean
  show_deleted?: boolean
  skip?: number
  limit?: number
}
